<template>
  <el-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :title="isEdit ? '샘플요청 수정' : '샘플요청 생성'"
    width="700px"
    destroy-on-close
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="110px"
      v-loading="loading"
    >
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="브랜드" prop="brand">
            <el-select v-model="form.brand" placeholder="브랜드 선택" filterable style="width: 100%" @change="handleBrandChange">
              <el-option
                v-for="brand in brands"
                :key="brand.id"
                :label="brand.managing_company_name ? `${brand.name} · ${brand.managing_company_name}` : brand.name"
                :value="brand.id"
              />
            </el-select>
            <div v-if="form.brand" class="company-hint" :class="{ 'company-warn': !selectedBrandObj?.managing_company }">
              담당법인: {{ selectedBrandObj?.managing_company_name || '미지정 (고객사/브랜드 관리에서 지정)' }}
            </div>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="디자인" prop="design">
            <el-select v-model="form.design" placeholder="디자인 선택" filterable style="width: 100%">
              <el-option
                v-for="design in filteredDesigns"
                :key="design.id"
                :label="getDesignLabel(design)"
                :value="design.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="샘플유형" prop="sample_type">
            <el-select v-model="form.sample_type" placeholder="유형 선택">
              <el-option label="프로토 (초기 개발)" value="PROTO" />
              <el-option label="세일즈맨 (영업용)" value="SALESMAN" />
              <el-option label="PP샘플 (생산전 확인)" value="PP" />
              <el-option label="TOP샘플 (최종 확인)" value="TOP" />
              <el-option label="AD샘플 (광고/촬영용)" value="AD" />
              <el-option label="기타" value="OTHER" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="수량" prop="quantity">
            <el-input-number v-model="form.quantity" :min="1" :max="999" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="요청일" prop="request_date">
            <el-date-picker
              v-model="form.request_date"
              type="date"
              placeholder="요청일 선택"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="납기일" prop="due_date">
            <el-date-picker
              v-model="form.due_date"
              type="date"
              placeholder="납기일 선택"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="색상">
            <el-input v-model="form.colors" placeholder="예: WHITE, BLACK, NAVY" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="사이즈">
            <el-input v-model="form.sizes" placeholder="예: S, M, L, XL" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="담당MD(자사)">
            <el-select v-model="form.designer" placeholder="담당 직원 선택" clearable filterable :loading="mdLoading">
              <el-option
                v-for="emp in designerOptions"
                :key="emp.id"
                :label="emp.name"
                :value="emp.id"
              />
            </el-select>
            <div v-if="mdHint" class="company-hint" :class="{ 'company-warn': mdHintWarn }">{{ mdHint }}</div>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="예상비용">
            <el-input-number
              v-model="form.estimated_cost"
              :min="0"
              :precision="0"
              :step="10000"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="메모">
        <el-input
          v-model="form.memo"
          type="textarea"
          :rows="3"
          placeholder="메모 입력"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">취소</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitting">
        {{ isEdit ? '수정' : '생성' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  modelValue: Boolean,
  editData: { type: Object, default: null },
  defaultData: { type: Object, default: null }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const formRef = ref(null)
const loading = ref(false)
const submitting = ref(false)
const brands = ref([])
const designs = ref([])
const employees = ref([])

const isEdit = ref(false)

const getDesignLabel = (design) => {
  const parts = []
  if (design.style_number) parts.push(design.style_number)
  if (design.main_style_number) parts.push(`(${design.main_style_number})`)
  if (design.product_name) parts.push(design.product_name)
  if (design.brand_name) parts.push(`[${design.brand_name}]`)
  return parts.length > 0 ? parts.join(' ') : `Design #${design.id}`
}

const getEmployeeLabel = (emp) => {
  const name = emp.get_full_name || emp.name || `${emp.last_name || ''}${emp.first_name || ''}`
  if (emp.department_name) return `${name} (${emp.department_name})`
  return name || `ID: ${emp.id}`
}

const filteredDesigns = computed(() => {
  if (!form.brand) return designs.value
  return designs.value.filter(d => d.brand === form.brand)
})

// 선택 브랜드 객체 → 담당법인 자동 결정 (customer-app-design.md §8)
const selectedBrandObj = computed(() =>
  brands.value.find(b => b.id === form.brand) || null
)

const handleBrandChange = () => {
  // 브랜드 변경 시, 선택된 디자인이 해당 브랜드에 속하지 않으면 초기화
  if (form.design) {
    const designExists = filteredDesigns.value.some(d => d.id === form.design)
    if (!designExists) {
      form.design = null
    }
  }
  // 법인은 브랜드 담당법인으로 자동 설정 (별도 선택 불필요)
  form.company = selectedBrandObj.value?.managing_company || null
  fetchMdAssignees(form.brand)
}

// ── 담당MD: 브랜드 담당자(MD 역할) 우선, 없으면 담당법인 직원 (customer-app-design.md §8) ──
const mdAssignees = ref([])
const mdLoading = ref(false)
const fetchMdAssignees = async (brandId) => {
  mdAssignees.value = []
  if (!brandId) return
  mdLoading.value = true
  try {
    const res = await axiosInstance.get('brand-assignments/', {
      params: { brand: brandId, role: 'MD', is_active: 'true' },
    })
    mdAssignees.value = res.data.results || res.data || []
    // MD 담당이 1명이면 자동 선택 (기존 값 없을 때만 — 수정 모드 보호)
    if (!form.designer && mdAssignees.value.length === 1) {
      form.designer = mdAssignees.value[0].employee
    }
  } catch (e) {
    console.error('브랜드 MD 담당 조회 실패:', e)
  } finally {
    mdLoading.value = false
  }
}

const designerOptions = computed(() => {
  let opts
  if (mdAssignees.value.length) {
    opts = mdAssignees.value.map(a => ({ id: a.employee, name: a.employee_name }))
  } else if (selectedBrandObj.value?.managing_company) {
    opts = employees.value
      .filter(e => e.company === selectedBrandObj.value.managing_company)
      .map(e => ({ id: e.id, name: getEmployeeLabel(e) }))
  } else {
    opts = employees.value.map(e => ({ id: e.id, name: getEmployeeLabel(e) }))
  }
  // 기존 저장값이 목록 밖이면 유지 표시 (담당 변경 후 과거 요청 수정 시)
  if (form.designer && !opts.some(o => o.id === form.designer)) {
    const cur = employees.value.find(e => e.id === form.designer)
    if (cur) opts = [{ id: cur.id, name: `${getEmployeeLabel(cur)} (현재 담당 아님)` }, ...opts]
  }
  return opts
})

const mdHint = computed(() => {
  if (!form.brand || mdLoading.value) return ''
  if (mdAssignees.value.length) return `브랜드 MD 담당 ${mdAssignees.value.length}명`
  if (selectedBrandObj.value?.managing_company) {
    return 'MD 미배정 — 담당법인 직원 표시 (브랜드 담당자 관리에서 배정 권장)'
  }
  return 'MD 미배정·담당법인 미지정 — 전체 직원 표시'
})
const mdHintWarn = computed(() => !!form.brand && !mdLoading.value && !mdAssignees.value.length)

const getToday = () => {
  const today = new Date()
  return today.toISOString().split('T')[0]
}

const form = reactive({
  company: null,
  brand: null,
  design: null,
  sample_type: 'PROTO',
  quantity: 1,
  request_date: getToday(),
  due_date: null,
  colors: '',
  sizes: '',
  designer: null,
  estimated_cost: 0,
  memo: ''
})

const rules = {
  brand: [{ required: true, message: '브랜드를 선택하세요', trigger: 'change' }],
  design: [{ required: true, message: '디자인을 선택하세요', trigger: 'change' }],
  sample_type: [{ required: true, message: '샘플유형을 선택하세요', trigger: 'change' }],
  quantity: [{ required: true, message: '수량을 입력하세요', trigger: 'blur' }],
  request_date: [{ required: true, message: '요청일을 선택하세요', trigger: 'change' }],
  due_date: [{ required: true, message: '납기일을 선택하세요', trigger: 'change' }]
}

const fetchBrands = async () => {
  try {
    const response = await axiosInstance.get('brands/')
    brands.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch brands:', error)
  }
}

const fetchDesigns = async () => {
  try {
    const response = await axiosInstance.get('designs/')
    designs.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch designs:', error)
  }
}

const fetchEmployees = async () => {
  try {
    const response = await axiosInstance.get('employees/')
    employees.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch employees:', error)
  }
}

const resetForm = () => {
  form.company = null
  form.brand = null
  form.design = null
  form.sample_type = 'PROTO'
  form.quantity = 1
  form.request_date = getToday()
  form.due_date = null
  form.colors = ''
  form.sizes = ''
  form.designer = null
  form.estimated_cost = 0
  form.memo = ''
  isEdit.value = false
}

const handleSubmit = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()
    submitting.value = true

    const data = { ...form }

    if (isEdit.value && props.editData?.id) {
      await axiosInstance.put(`sample-requests/${props.editData.id}/`, data)
      ElMessage.success('샘플요청이 수정되었습니다.')
    } else {
      await axiosInstance.post('sample-requests/', data)
      ElMessage.success('샘플요청이 생성되었습니다.')
    }

    emit('update:modelValue', false)
    emit('saved')
  } catch (error) {
    if (error !== 'cancel' && error !== false) {
      console.error('Submit failed:', error)
      ElMessage.error(error.response?.data?.detail || '저장 중 오류가 발생했습니다.')
    }
  } finally {
    submitting.value = false
  }
}

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    if (props.editData) {
      isEdit.value = true
      Object.assign(form, props.editData)
    } else {
      resetForm()
      if (props.defaultData) {
        if (props.defaultData.brand) form.brand = props.defaultData.brand
        if (props.defaultData.design) form.design = props.defaultData.design
      }
    }
    // 브랜드 로드 후 company 자동 보정 + MD 담당 조회 (수정/기본값 진입 시에도 동기화)
    mdAssignees.value = []
    fetchBrands().then(() => {
      if (form.brand) {
        if (!form.company) form.company = selectedBrandObj.value?.managing_company || null
        fetchMdAssignees(form.brand)
      }
    })
    fetchDesigns()
    fetchEmployees()
  }
})
</script>

<style scoped>
.el-select {
  width: 100%;
}
.company-hint {
  font-size: 11px;
  color: #909399;
  line-height: 1.4;
  margin-top: 2px;
}
.company-hint.company-warn {
  color: #e6a23c;
}
</style>
